import { z } from "zod";
import { RichText } from "../RichText";
import { bgClasses } from "../bgClasses";
import { ctaColorEnum, ctaClasses } from "../ctaColors";
import { globalSectionSchema, overlayOpacityEnum, OVERLAY_CLASSES, normalizeBgPosition, TEXT_MEASURE } from "../globalProps";
import { IMAGE_SHAPE_CLASSES } from "../imageShapes";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Welcome"),
  subheading: z.string().optional(),
  /* Measure for the subheading. Every other section hardcodes the "medium"
     value; Hero makes it authorable because hero copy varies most in length. */
  textWidth: z.enum(["narrow", "medium", "full"]).default("medium"),
  ctaLabel: z.string().optional(),
  ctaHref: z.string().optional(),
  alignment: z.enum(["left", "center"]).default("center"),
  layout: z
    .enum(["centered", "left", "split", "image", "banner"])
    .default("centered"),
  imageUrl: z.string().optional(),
  imageOriginalUrl: z.string().optional(),
  imageOriginalStorageKey: z.string().optional(),
  imageCrop: z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(), zoom: z.number(),
  }).optional(),
  backgroundImageOriginalUrl: z.string().optional(),
  backgroundImageOriginalStorageKey: z.string().optional(),
  backgroundImageCrop: z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(), zoom: z.number(),
  }).optional(),
  imagePosition: z.enum(["left", "right"]).default("right"),
  imageAlt: z.string().default(""),
  imageShape: z.enum(["square", "rounded"]).default("rounded"),
  ctaTarget: z.enum(["_self", "_blank"]).default("_self"),
  ctaStyle: z.enum(["solid", "outline", "ghost"]).default("solid"),
  ctaColor: ctaColorEnum.optional(),
  secondaryCtaLabel: z.string().optional(),
  secondaryCtaHref: z.string().optional(),
  secondaryCtaStyle: z.enum(["solid", "outline", "ghost"]).default("outline"),
  secondaryCtaColor: ctaColorEnum.optional(),
  secondaryCtaTarget: z.enum(["_self", "_blank"]).default("_self"),
  overlayOpacity: overlayOpacityEnum.default("medium"),
  /* Hero defaults: h1 for heading, normal on hero density scale */
  headingLevel: z.enum(["h1", "h2", "h3"]).default("h1"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* Subheading measure. `center` adds mx-auto so a constrained block stays under
   centered copy; left-aligned and flex-column layouts must stay flush. */
function subMeasure(safe: Props, center: boolean): string {
  const m = TEXT_MEASURE[safe.textWidth];
  if (!m) return "";
  return center ? `${m} mx-auto` : m;
}

/* ---------- CTA button (reused across variants) ---------- */

function CtaButton({
  label,
  href,
  inverted,
  target,
  ctaStyle = "solid",
  color = "primary",
  dataProp = "ctaLabel",
}: {
  label: string;
  href: string;
  inverted?: boolean;
  target?: string;
  ctaStyle?: "solid" | "outline" | "ghost";
  color?: string;
  dataProp?: string;
}) {
  const base = "inline-block font-semibold px-8 py-3 rounded-lg transition-all";
  const colorClasses = ctaClasses(color, ctaStyle, inverted);

  return (
    <a
      href={href}
      className={`${base} ${colorClasses}`}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
    >
      <RichText html={label} data-prop={dataProp} />
    </a>
  );
}

/* ---------- Determine effective layout ---------- */

/**
 * For backward compatibility: existing pages have no `layout` prop but may
 * have `alignment: "left"`. We map that to the "left" layout automatically.
 * If `layout` is explicitly set, it takes precedence.
 */
function effectiveLayout(safe: Props): Props["layout"] {
  // If layout is explicitly set to something other than the default, use it
  if (safe.layout !== "centered") return safe.layout;
  // Legacy: alignment: "left" with no explicit layout maps to "left"
  if (safe.alignment === "left") return "left";
  return "centered";
}

/* ---------- Layout: Centered (default) ---------- */

function CenteredLayout({ safe }: { safe: Props }) {
  const hasSecondary = !!(safe.secondaryCtaLabel && safe.secondaryCtaHref);
  return (
    <SectionShell globals={safe} densityScale="hero">
      <div className="relative text-center">
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-4xl @md:text-5xl font-bold mb-4"
          data-prop="heading"
        />
        {safe.subheading && (
          <RichText
            as="div"
            html={safe.subheading}
            className={`text-xl @md:text-2xl mb-8 opacity-90 ${subMeasure(safe, true)}`}
            data-prop="subheading"
          />
        )}
        {safe.ctaLabel && safe.ctaHref && (
          <div className="flex flex-wrap items-center justify-center gap-4">
            <CtaButton label={safe.ctaLabel} href={safe.ctaHref} target={safe.ctaTarget} ctaStyle={safe.ctaStyle} color={safe.ctaColor || "primary"} />
            {hasSecondary && (
              <CtaButton label={safe.secondaryCtaLabel!} href={safe.secondaryCtaHref!} target={safe.secondaryCtaTarget} ctaStyle={safe.secondaryCtaStyle} color={safe.secondaryCtaColor || safe.ctaColor || "primary"} dataProp="secondaryCtaLabel" />
            )}
          </div>
        )}
      </div>
    </SectionShell>
  );
}

/* ---------- Layout: Left-aligned ---------- */

function LeftLayout({ safe }: { safe: Props }) {
  const hasSecondary = !!(safe.secondaryCtaLabel && safe.secondaryCtaHref);
  return (
    <SectionShell globals={safe} densityScale="hero">
      <div className="relative text-left">
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-4xl @md:text-5xl font-bold mb-4"
          data-prop="heading"
        />
        {safe.subheading && (
          <RichText
            as="div"
            html={safe.subheading}
            className={`text-xl @md:text-2xl mb-8 opacity-90 ${subMeasure(safe, false)}`}
            data-prop="subheading"
          />
        )}
        {safe.ctaLabel && safe.ctaHref && (
          <div className="flex flex-wrap items-center gap-4">
            <CtaButton label={safe.ctaLabel} href={safe.ctaHref} target={safe.ctaTarget} ctaStyle={safe.ctaStyle} color={safe.ctaColor || "primary"} />
            {hasSecondary && (
              <CtaButton label={safe.secondaryCtaLabel!} href={safe.secondaryCtaHref!} target={safe.secondaryCtaTarget} ctaStyle={safe.secondaryCtaStyle} color={safe.secondaryCtaColor || safe.ctaColor || "primary"} dataProp="secondaryCtaLabel" />
            )}
          </div>
        )}
      </div>
    </SectionShell>
  );
}

/* ---------- Layout: Split (two-column text + image) ---------- */

function SplitLayout({ safe }: { safe: Props }) {
  const imgShape = IMAGE_SHAPE_CLASSES[safe.imageShape] || IMAGE_SHAPE_CLASSES.rounded;
  const hasSecondary = !!(safe.secondaryCtaLabel && safe.secondaryCtaHref);

  const textSide = (
    <div className="flex flex-col justify-center">
      <RichText
        as={safe.headingLevel}
        html={safe.heading}
        className="text-4xl @md:text-5xl font-bold mb-4"
        data-prop="heading"
      />
      {safe.subheading && (
        <RichText
          as="div"
          html={safe.subheading}
          className={`text-xl @md:text-2xl mb-8 opacity-90 ${subMeasure(safe, false)}`}
          data-prop="subheading"
        />
      )}
      {safe.ctaLabel && safe.ctaHref && (
        <div className="flex flex-wrap items-center gap-4">
          <CtaButton label={safe.ctaLabel} href={safe.ctaHref} target={safe.ctaTarget} ctaStyle={safe.ctaStyle} color={safe.ctaColor || "primary"} />
          {hasSecondary && (
            <CtaButton label={safe.secondaryCtaLabel!} href={safe.secondaryCtaHref!} target={safe.secondaryCtaTarget} ctaStyle={safe.secondaryCtaStyle} color={safe.secondaryCtaColor || safe.ctaColor || "primary"} dataProp="secondaryCtaLabel" />
          )}
        </div>
      )}
    </div>
  );

  const imageSide = (
    <div className={`${imgShape} overflow-hidden`}>
      {safe.imageUrl ? (
        <img
          src={safe.imageUrl}
          alt={safe.imageAlt || ""}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className={`aspect-[4/3] bg-surface ${imgShape}`} />
      )}
    </div>
  );

  return (
    <SectionShell globals={safe} densityScale="hero">
        <div className="grid grid-cols-1 @md:grid-cols-2 gap-10 @md:gap-16 items-center">
          {safe.imagePosition === "left" ? (
            <>
              {imageSide}
              {textSide}
            </>
          ) : (
            <>
              {textSide}
              {imageSide}
            </>
          )}
        </div>
    </SectionShell>
  );
}

/* ---------- Layout: Image (full-bleed background with overlay) ---------- */

function ImageLayout({ safe }: { safe: Props }) {
  const overlayClass = OVERLAY_CLASSES[safe.overlayOpacity] || OVERLAY_CLASSES.medium;
  const align = safe.alignment === "left" ? "text-left" : "text-center";
  const justifyClass = safe.alignment === "left" ? "" : "justify-center";
  const hasSecondary = !!(safe.secondaryCtaLabel && safe.secondaryCtaHref);
  return (
    <SectionShell
      globals={safe}
      densityScale="hero"
      className="relative"
      style={
        safe.backgroundImageUrl
          ? {
              backgroundImage: `url(${safe.backgroundImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: normalizeBgPosition(safe.backgroundPosition),
            }
          : undefined
      }
      bare
    >
      {/* Dark overlay -- only when opacity is not "none" */}
      {safe.overlayOpacity !== "none" && (
        <div className={`absolute inset-0 ${overlayClass}`} />
      )}

      {/* Fallback background when no image is uploaded */}
      {!safe.backgroundImageUrl && (
        <div className="absolute inset-0 bg-accent" />
      )}

      {/* w-full: the full-height section is a flex column, and mx-auto on a
          flex item overrides cross-axis stretch — without an explicit width
          the wrapper shrink-wraps to its content and floats to center. */}
      <div className={`relative w-full max-w-6xl mx-auto px-6 ${align} text-white`}>
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-4xl @md:text-6xl font-bold mb-4"
          data-prop="heading"
        />
        {safe.subheading && (
          <RichText
            as="div"
            html={safe.subheading}
            className={`text-xl @md:text-2xl mb-8 opacity-90 ${subMeasure(safe, safe.alignment !== "left")}`}
            data-prop="subheading"
          />
        )}
        {safe.ctaLabel && safe.ctaHref && (
          <div className={`flex flex-wrap items-center gap-4 ${justifyClass}`}>
            <CtaButton label={safe.ctaLabel} href={safe.ctaHref} inverted={!safe.ctaColor} target={safe.ctaTarget} ctaStyle={safe.ctaStyle} color={safe.ctaColor || "primary"} />
            {hasSecondary && (
              <CtaButton label={safe.secondaryCtaLabel!} href={safe.secondaryCtaHref!} target={safe.secondaryCtaTarget} ctaStyle={safe.secondaryCtaStyle} color={safe.secondaryCtaColor || safe.ctaColor || "primary"} dataProp="secondaryCtaLabel" />
            )}
          </div>
        )}
      </div>
    </SectionShell>
  );
}

/* ---------- Layout: Banner (short announcement strip) ---------- */

function BannerLayout({ safe }: { safe: Props }) {
  const bg = bgClasses[safe.backgroundStyle] || bgClasses.surface;
  const dividerTop = safe.topDivider ? "border-t border-current/10" : "";
  const dividerBottom = safe.bottomDivider ? "border-b border-current/10" : "";
  const mobileHide = safe.hideOnMobile ? "hidden @md:block" : "";

  return (
    <section
      className={[
        "py-4 @md:py-6",
        bg,
        dividerTop,
        dividerBottom,
        mobileHide,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="max-w-6xl mx-auto px-6 flex flex-col @md:flex-row items-center justify-center gap-4 text-center">
        <RichText
          as="p"
          html={safe.heading}
          className="text-base @md:text-lg font-medium"
          data-prop="heading"
        />
        {safe.ctaLabel && safe.ctaHref && (
          <a
            href={safe.ctaHref}
            target={safe.ctaTarget}
            rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
            className="inline-block bg-white/20 backdrop-blur font-semibold px-5 py-1.5 rounded-full text-sm hover:bg-white/30 transition-colors"
          >
            <RichText html={safe.ctaLabel} data-prop="ctaLabel" />
          </a>
        )}
      </div>
    </section>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const layout = effectiveLayout(safe);

  switch (layout) {
    case "left":
      return <LeftLayout safe={safe} />;
    case "split":
      return <SplitLayout safe={safe} />;
    case "image":
      return <ImageLayout safe={safe} />;
    case "banner":
      return <BannerLayout safe={safe} />;
    case "centered":
    default:
      return <CenteredLayout safe={safe} />;
  }
}
